import { getOrganizationUnitIcon } from '@shared/utils/organization-unit-icon.util';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { OrganizationLevel, OrganizationUnit } from '../models/unit.model';
import { UnitService } from '../services/unit.service';
import { buildCommunityHierarchy } from './unit-communities';

@Component({
  selector: 'app-unit-communities-page',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule, RouterLink],
  template: `
    <section class="communities-page">
      <h2>Comunidades</h2>
      <div *ngIf="isLoading" class="loading-state"><mat-spinner diameter="32"></mat-spinner></div>
      <div *ngIf="!isLoading && errorMessage" class="error-state" role="alert">{{ errorMessage }}</div>
      <div *ngIf="!isLoading && !errorMessage && hierarchy.length === 0" class="empty-state">
        <mat-icon>church</mat-icon>
        <p>Esta unidad todavía no tiene comunidades asociadas.</p>
      </div>
      <div *ngIf="!isLoading && !errorMessage && hierarchy.length > 0" class="community-list">
        <ng-container *ngTemplateOutlet="communityTree; context: { $implicit: hierarchy, depth: 0 }"></ng-container>
      </div>
      <ng-template #communityTree let-units let-depth="depth">
        <ng-container *ngFor="let unit of units">
          <section *ngIf="unit.organizationLevelId !== communityLevel; else communityCard" class="hierarchy-group" [class.area-group]="unit.organizationLevelId === areaLevel" [class.district-group]="unit.organizationLevelId === districtLevel">
            <div class="group-heading" role="heading" [attr.aria-level]="depth + 3">
              <a class="group-link" [routerLink]="['/units', unit.id]">
              <mat-icon *ngIf="getUnitIcon(unit.organizationLevelId) !== 'church-group'">{{ getUnitIcon(unit.organizationLevelId) }}</mat-icon>
              <mat-icon *ngIf="getUnitIcon(unit.organizationLevelId) === 'church-group'" svgIcon="church-group"></mat-icon>
              <span class="group-name">{{ unit.name }}</span>
              <span class="group-level">{{ unit.levelName }}</span>
              </a>
              <span class="group-leader">{{ unit.leader ? 'Dirigente: ' + unit.leader : 'Sin dirigente asignado' }}</span>
              <span class="group-count">{{ communityCounts.get(unit.id) }} {{ communityCounts.get(unit.id) === 1 ? 'comunidad' : 'comunidades' }}</span>
              <button class="group-toggle" type="button" (click)="toggleGroup(unit.id)"
                [attr.aria-expanded]="!collapsedGroups.has(unit.id)" [attr.aria-controls]="'community-group-' + unit.id"
                [attr.aria-label]="(collapsedGroups.has(unit.id) ? 'Expandir ' : 'Colapsar ') + unit.name">
                <mat-icon [class.is-collapsed]="collapsedGroups.has(unit.id)">expand_less</mat-icon>
              </button>
            </div>
            <div class="group-content" [class.is-collapsed]="collapsedGroups.has(unit.id)" [id]="'community-group-' + unit.id"
              [attr.inert]="collapsedGroups.has(unit.id) ? '' : null" [attr.aria-hidden]="collapsedGroups.has(unit.id)">
              <div class="group-content-inner">
                <div class="community-list group-children">
                  <ng-container *ngTemplateOutlet="communityTree; context: { $implicit: unit.children, depth: depth + 1 }"></ng-container>
                </div>
              </div>
            </div>
          </section>
          <ng-template #communityCard>
            <button class="community-card" type="button" (click)="openCommunity(unit.id)">
              <span class="community-icon"><mat-icon>church</mat-icon></span>
              <span class="community-details">
                <strong>{{ unit.name }}</strong>
                <small *ngIf="unit.leader">Dirigente: {{ unit.leader }}</small>
                <small *ngIf="!unit.leader">Sin dirigente asignado</small>
              </span>
              <mat-icon class="open-icon">chevron_right</mat-icon>
            </button>
          </ng-template>
        </ng-container>
      </ng-template>
    </section>
  `,
  styles: [`
    :host { display: block; min-height: 100%; }
    .communities-page { padding: 20px; }
    h2 { margin: 0 0 16px; font-size: 20px; }
    .community-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr)); gap: 12px; }
    .hierarchy-group { grid-column: 1 / -1; min-width: 0; }
    .hierarchy-group + .hierarchy-group { margin-top: 8px; }
    .group-heading { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 2px 8px 2px 12px; border-radius: 10px; background: #f0f5fa; color: #255581; }
    .group-link { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-width: 0; padding: 10px 0; color: inherit; text-decoration: none; }
    .group-link:hover .group-name { text-decoration: underline; }
    .group-link > mat-icon { flex-shrink: 0; width: 20px; height: 20px; font-size: 20px; line-height: 20px; color: #4176a6; }
    .group-toggle { display: grid; place-items: center; flex-shrink: 0; width: 44px; height: 44px; border: 0; border-radius: 50%; background: transparent; color: inherit; cursor: pointer; }
    .group-toggle:hover { background: #d5e3f2; }
    .group-toggle mat-icon { transition: transform 240ms ease; }
    .group-toggle mat-icon.is-collapsed { transform: rotate(180deg); }
    .group-content { display: grid; grid-template-rows: 1fr; opacity: 1; visibility: visible; transition: grid-template-rows 240ms ease, opacity 240ms ease, visibility 0s; }
    .group-content.is-collapsed { grid-template-rows: 0fr; opacity: 0; visibility: hidden; transition: grid-template-rows 240ms ease, opacity 240ms ease, visibility 0s 240ms; }
    .group-content-inner { min-height: 0; overflow: hidden; }
    .group-leader { margin-left: auto; min-width: 0; font-size: 12px; color: #5d7186; overflow-wrap: anywhere; }
    .group-count { flex-shrink: 0; padding: 4px 8px; border-radius: 12px; background: #fff9; font-size: 12px; white-space: nowrap; }
    .group-name { font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
    .group-level { font-size: 12px; color: #5d7186; }
    .area-group > .group-heading { background: #dfeaf6; }
    .district-group > .group-heading { background: #e8f0f8; }
    .group-children { margin-left: 10px; padding-left: 14px; border-left: 2px solid #e2ebf4; }
    .community-card:focus-visible, .group-link:focus-visible, .group-toggle:focus-visible { outline: 2px solid #1675e8; outline-offset: 2px; }
    @media (max-width: 600px) {
      .communities-page { padding: 12px 4px; }
      .group-children { margin-left: 4px; padding-left: 8px; }
      .group-heading { flex-wrap: wrap; gap: 4px 8px; padding-bottom: 8px; }
      .group-link { flex: 1; }
      .group-leader { order: 2; flex-basis: 100%; margin-left: 0; }
      .group-count { order: 3; }
    }
    @media (prefers-reduced-motion: reduce) {
      .group-content, .group-content.is-collapsed, .group-toggle mat-icon { transition: none; }
    }
    .community-card { display: flex; align-items: center; gap: 12px; min-width: 0; padding: 14px; border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; text-align: left; cursor: pointer; }
    .community-card:hover { border-color: #1675e8; background: #f8fbff; }
    .community-icon { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: #e7f4fa; color: #1683a5; flex: 0 0 auto; }
    .community-details { display: flex; flex: 1; flex-direction: column; gap: 4px; min-width: 0; }
    .community-details strong { overflow-wrap: anywhere; }
    .community-details small { color: #667085; }
    .open-icon { color: #667085; }
    .loading-state, .empty-state, .error-state { display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 180px; text-align: center; }
    .empty-state { flex-direction: column; color: #667085; }
    .empty-state mat-icon { width: 36px; height: 36px; font-size: 36px; }
    .error-state { color: #b42318; }
  `],
})
export class UnitCommunitiesPageComponent implements OnInit, OnDestroy {
  hierarchy: OrganizationUnit[] = [];
  readonly collapsedGroups = new Set<string>();
  readonly communityCounts = new Map<string, number>();
  readonly communityLevel = OrganizationLevel.Comunidad;
  readonly areaLevel = OrganizationLevel.AreaApostol;
  readonly districtLevel = OrganizationLevel.Distrito;
  readonly getUnitIcon = getOrganizationUnitIcon;
  isLoading = true;
  errorMessage: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly unitService: UnitService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const unitId = this.route.pathFromRoot.reduce((id, route) => id ?? route.snapshot.paramMap.get('unit-id'), null as string | null);
    if (!unitId) {
      this.fail('No se pudo abrir la unidad solicitada.');
      return;
    }
    this.unitService.getChildUnitsNested(unitId).pipe(takeUntil(this.destroy$)).subscribe({
      next: children => {
        this.hierarchy = buildCommunityHierarchy(children);
        this.communityCounts.clear();
        this.hierarchy.forEach(unit => this.countCommunities(unit));
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => this.fail('No se pudieron cargar las comunidades de esta unidad.'),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openCommunity(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  toggleGroup(unitId: string): void {
    if (this.collapsedGroups.has(unitId)) {
      this.collapsedGroups.delete(unitId);
    } else {
      this.collapsedGroups.add(unitId);
    }
  }

  private countCommunities(unit: OrganizationUnit): number {
    const count = unit.organizationLevelId === this.communityLevel
      ? 1
      : (unit.children ?? []).reduce((total, child) => total + this.countCommunities(child), 0);
    this.communityCounts.set(unit.id, count);
    return count;
  }

  private fail(message: string): void {
    this.errorMessage = message;
    this.isLoading = false;
    this.cdr.markForCheck();
  }
}
